/**
 * helpers.js — Utilidades puras de rendering para jsPDF
 */
// En dev: URL relativa → proxy CRA a localhost:8080
// En prod (Vercel): REACT_APP_API_URL apunta al backend real
// REACT_APP_API_URL termina en /api (ej: https://api.gestiondlt.com/api)
// Subimos un nivel para construir la URL de uploads
const API_BASE     = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const UPLOADS_BASE = `${API_BASE}/api/uploads`;

// Contador de fotos que fallaron al cargar (reseteado por cada generación de PDF)
let _fotosConError = 0;
export function resetFotosConError() { _fotosConError = 0; _cacheFotos.clear(); }
export function getFotosConError()   { return _fotosConError; }

// ── Texto ─────────────────────────────────────────────────────────────────────

// Elimina patrones internos del sistema que no deben verse en el PDF
// Ej: "| MO: $60000" es formato interno de costoExtra, ya se muestra en tabla de precios
function limpiarInterno(texto) {
    if (!texto) return '';
    return texto
        .replace(/\|\s*MO:\s*\$[\d.,]+/gi, '')
        .replace(/^\s*\|\s*/, '')
        .trim();
}

export function sanitizarTexto(texto, maxLen = 400) {
    if (!texto) return '';
    return limpiarInterno(texto)
        .replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim()
        .substring(0, maxLen)
        .split(' ').map(w => w.length > 30 ? w.substring(0, 28) + '…' : w).join(' ');
}

export function sanitizarCorto(texto, maxLen = 110) {
    if (!texto) return '';
    return texto.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim().substring(0, maxLen);
}

export function truncarLineas(doc, texto, maxWidth, maxLineas) {
    if (!texto) return [];
    const todas = doc.splitTextToSize(texto, maxWidth);
    if (todas.length <= maxLineas) return todas;
    const cortadas = todas.slice(0, maxLineas);
    const ultima = cortadas[maxLineas - 1];
    cortadas[maxLineas - 1] = ultima.length > 4 ? ultima.substring(0, ultima.length - 3) + '...' : '...';
    return cortadas;
}

// Construye línea de metadata con prioridad sin superponer precio
export function buildMetaLinea(doc, modelo, serial, ubicacion, garantia, maxW, fontSize) {
    doc.setFontSize(fontSize);
    const mide = s => doc.getStringUnitWidth(s) * fontSize / doc.internal.scaleFactor;
    const base = modelo || 'Dispenser';
    const partes = [
        serial    ? `N/S: ${serial}`      : null,
        ubicacion ? `Ubic.: ${ubicacion}` : null,
        garantia  ? `Gta: ${garantia}`    : null,
    ].filter(Boolean);
    let linea = base;
    for (const p of partes) {
        const candidato = linea + '  ·  ' + p;
        if (mide(candidato) <= maxW) linea = candidato;
        else break;
    }
    if (mide(linea) > maxW) {
        let s = linea;
        while (s.length > 4 && mide(s + '...') > maxW) s = s.slice(0, -1);
        linea = s + '...';
    }
    return linea;
}

// ── Fotos ─────────────────────────────────────────────────────────────────────

// Resolución máxima para fotos en PDF (reduce tamaño y memoria significativamente)
// Por defecto 800px — suficiente para fotos grandes (tier 1, ~70mm a 150dpi ≈ 413px)
const PDF_MAX_DEFAULT = 900;

function resolverDimensiones(nw, nh, maxPx = PDF_MAX_DEFAULT) {
    let w = nw || maxPx;
    let h = nh || maxPx;
    const mayor = Math.max(w, h);
    if (mayor > maxPx) {
        const ratio = maxPx / mayor;
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
    }
    return { w, h };
}

// Convierte cualquier blob/File a JPEG via canvas (soporta WebP, HEIC, etc.)
// maxPx controla la resolución máxima del lado mayor, quality la compresión JPEG
async function blobAJpeg(blob, maxPx = PDF_MAX_DEFAULT, quality = 0.75) {
    return new Promise(resolve => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            try {
                const { w, h } = resolverDimensiones(img.naturalWidth, img.naturalHeight, maxPx);
                const canvas = document.createElement('canvas');
                canvas.width  = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                const dataUrl = canvas.toDataURL('image/jpeg', quality);
                URL.revokeObjectURL(url);
                resolve({ dataUrl, w, h });
            } catch { URL.revokeObjectURL(url); resolve(null); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
}

// Ajusta dimensiones de imagen para caber en una caja máxima manteniendo proporción
export function fitEnCaja(imgW, imgH, maxW, maxH) {
    if (!imgW || !imgH) return { w: maxW, h: maxH };
    const ratio = Math.min(maxW / imgW, maxH / imgH);
    return { w: imgW * ratio, h: imgH * ratio };
}

// maxPx: resolución máxima del lado mayor (default 800)
// quality: compresión JPEG 0-1 (default 0.75)
// Cache por PDF (13-oct-2026): la misma foto se usaba en la hoja del trabajo y en la de evidencia
// y se bajaba dos veces desde el servidor. Se limpia con resetFotosConError() al empezar cada PDF.
const _cacheFotos = new Map();
const ESPERA_MAX_MS = 15000;   // una foto que no llega no frena el PDF para siempre

// Precarga (28-oct-2026): al abrir la ficha de un trabajo se bajan sus fotos, así al tocar "PDF" el archivo
// sale enseguida. Si tarda más de ~5 s el celular ya no deja descargar desde ese toque y había que tocar dos
// veces. No se borra al empezar cada PDF (resetFotosConError); la que falla se saca para volver a pedirla.
const _blobs = new Map();
const nombreFoto = (src) => (typeof src === 'string' && src.startsWith('http') ? src.split('/').pop() : src);

export function precargarFotos(lista) {
    for (const src of lista || []) {
        if (typeof src !== 'string' || !src || src.startsWith('data:')) continue;
        const f = nombreFoto(src);
        if (_blobs.has(f)) continue;
        _blobs.set(f, fetch(`${UPLOADS_BASE}/${f}`).then(r => (r.ok ? r.blob() : null)).catch(() => null)
            .then(b => { if (!b) _blobs.delete(f); return b; }));
    }
}

export async function cargarFoto(src, maxPx = PDF_MAX_DEFAULT, quality = 0.75) {
    if (!src) return null;
    const clave = typeof src === 'string' ? `${src.slice(0, 200)}|${src.length}|${maxPx}|${quality}` : null;
    if (clave && _cacheFotos.has(clave)) return _cacheFotos.get(clave);
    // 22-oct-2026: con datos móviles la foto (bajada desde R2 a través del backend) a veces no
    // llegaba a tiempo y salía el recuadro gris. Se reintenta una vez y la que falla no se guarda en
    // la cache, así el próximo PDF la vuelve a pedir.
    const promesa = cargarFotoSinCache(src, maxPx, quality)
        .then(r => r || cargarFotoSinCache(src, maxPx, quality, true))
        .then(r => { if (!r && clave) _cacheFotos.delete(clave); return r; });
    if (clave) _cacheFotos.set(clave, promesa);
    return promesa;
}

async function cargarFotoSinCache(src, maxPx, quality, reintento = false) {

    // Data URL → cargar directo en canvas sin pasar por fetch/blob.
    // fetch('data:...') puede fallar en móviles (Chrome Android) sin lanzar excepción
    // pero devolviendo un blob sin tipo MIME, lo que rompe el render en canvas.
    if (typeof src === 'string' && src.startsWith('data:')) {
        return new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                try {
                    const { w, h } = resolverDimensiones(img.naturalWidth, img.naturalHeight, maxPx);
                    const canvas = document.createElement('canvas');
                    canvas.width  = w;
                    canvas.height = h;
                    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                    resolve({ data: canvas.toDataURL('image/jpeg', quality), format: 'JPEG', w, h });
                } catch { resolve(null); }
            };
            img.onerror = () => { if (reintento) _fotosConError++; resolve(null); };
            img.src = src;
        });
    }

    let blob = null;

    if (src instanceof File) {
        blob = src;
    } else {
        try {
            // Filename o URL → proxy del backend (evita CORS con R2)
            const filename = nombreFoto(src);
            const pre = !reintento && _blobs.get(filename);
            if (pre) blob = await pre;
        } catch { blob = null; }
    }
    if (!blob && !(src instanceof File) && !(typeof src === 'string' && src.startsWith('data:'))) {
        try {
            const filename = nombreFoto(src);
            const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const corte = ctrl && setTimeout(() => ctrl.abort(), reintento ? ESPERA_MAX_MS * 2 : ESPERA_MAX_MS);
            const res = await fetch(`${UPLOADS_BASE}/${filename}`, ctrl ? { signal: ctrl.signal } : undefined);
            if (corte) clearTimeout(corte);
            if (!res.ok) { if (reintento) _fotosConError++; return null; }
            blob = await res.blob();
        } catch { if (reintento) _fotosConError++; return null; }
    }

    if (!blob) return null;

    // Convertir siempre a JPEG via canvas (jsPDF no soporta WebP/HEIC/etc.)
    const result = await blobAJpeg(blob, maxPx, quality);
    if (!result) return null;
    return { data: result.dataUrl, format: 'JPEG', w: result.w, h: result.h };
}

// ── Drawing primitives ────────────────────────────────────────────────────────

export function label(doc, txt, x, y, color = [75, 85, 99]) {
    doc.setFontSize(6);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...color);
    doc.text(txt.toUpperCase(), x, y);
}

export function value(doc, txt, x, y, size = 9.5, color = [17, 24, 39]) {
    doc.setFontSize(size);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...color);
    doc.text(String(txt), x, y);
}

// Chip / badge de estado
export function dibujarBadge(doc, texto, x, y, bgColor, textColor = [255,255,255]) {
    doc.setFontSize(6.5);
    doc.setFont(undefined, 'bold');
    const w = doc.getStringUnitWidth(texto) * 6.5 / doc.internal.scaleFactor + 8;
    doc.setFillColor(...bgColor);
    doc.roundedRect(x, y - 4.5, w, 6.5, 1.5, 1.5, 'F');
    doc.setTextColor(...textColor);
    doc.text(texto, x + w / 2, y, { align: 'center' });
    return w;
}

// Línea divisoria con estilo
export function divisor(doc, y, color = [209, 213, 219], pageW = 210, margin = 14) {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.25);
    doc.line(margin, y, pageW - margin, y);
}

// Verificar y saltar de página
export function checkSalto(doc, y, needed, yStart = 18) {
    const pageH = doc.internal.pageSize.getHeight();
    if (y + needed > pageH - 25) {
        doc.addPage();
        return yStart;
    }
    return y;
}
